<template>
  <v-card
    v-if="item && Object.keys(item).length > 0"
    flat
    class="printableArea font font-bill my-0"
    id="printBill"
    :outlined="outlined"
  >
    <v-card-text>
      <div class="a4-container">
        <div class="a4-table-container text-center">
          <div class="font default mb-1 text-h4">{{ head1 }}</div>
          <div class="font default mb-2 text-h4">{{ head2 }}</div>
        </div>
        <div class="a4-table-container text-center mb-3">
          *********00000*********
        </div>
        <div class="a4-table-container text-center">
          <h1 class="font font-weight-bold mb-3">{{ $t("receipt") }}</h1>
          <h1 class="font font-weight-bold display-1">Receipt</h1>
        </div>
        <div class="a4-header d-flex align-start justify-space-between">
          <div class="a4-store">
            <img
              v-if="Img && show_logo"
              width="120"
              :src="Img"
              alt="logo"
              class="logo mb-2"
            />
            <h2
              class="font mb-2"
              v-html="safeItem?.store_id?.name || $t('store_name')"
            ></h2>
            <div v-if="phoneMobileLine" class="font default mb-1">
              {{ phoneMobileLine }}
            </div>
            <!-- company information -->
            <div class="font default">
              <div
                v-if="show_store_address && safeItem?.store_id?.address"
                v-html="safeItem.store_id.address"
              ></div>
              <!-- email -->
              <div v-if="show_email && user?.email" class="mb-1">
                {{ $t("email") }}: {{ user.email }}
              </div>
            </div>
            <!-- customer information -->
          </div>
          <div class="a4-doc text-right">
            <div class="font default">
              {{ $t("code") }}:
              <span>{{ safeItem?.code || "" }}</span>
            </div>
            <div class="font default">
              {{ $t("date") }}: {{ safeItem?.created_at | moment }}
            </div>
            <div v-if="queueLabel" class="font mt-2">
              <div class="default">{{ $t("queue_number") }}</div>
              <div class="font-weight-black" style="font-size: 28px">
                {{ queueLabel }}
              </div>
            </div>
          </div>
        </div>

        <div class="a4-customer" v-if="item.customer_id">
          <div class="font default">
            {{ $t("customer") }}
            : {{ customer }}
          </div>

          <div
            class="font default"
            v-if="
              customer_address && (address || safeItem?.customer_id?.address)
            "
          >
            {{ $t("address") }}: {{ address || safeItem?.customer_id?.address }}
          </div>
          <div class="font default" v-if="customer_logistics">
            {{ $t("logistics_partner") }}: {{ customerLogistics }}
          </div>
        </div>

        <v-simple-table class="a4-table mt-3 border-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="font text-left" style="width: 60px">
                  {{ $t("no") }}
                </th>
                <th class="font text-left">{{ $t("item") }}</th>
                <th class="font text-center" style="width: 90px">
                  {{ $t("amount") }}
                </th>
                <th class="font text-center" style="width: 80px">
                  {{ $t("unit") }}
                </th>
                <th class="font text-center" style="width: 140px">
                  {{ $t("price") }}
                </th>
                <th class="font text-right" style="width: 160px">
                  {{ $t("total") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(data, index) in safeItem?.history || []"
                :key="`row-${index}`"
              >
                <td class="font text-center">{{ index + 1 }}</td>
                <td class="font text-left">
                  {{ getProductDisplayName(data) }}
                  <span
                    v-if="isStampRewardLine(safeItem, data)"
                    class="font stamp-reward-tag"
                  >
                    ({{ $t("stamp_paid_by_stamp") }})
                  </span>
                  <div
                    v-if="
                      data.bundle_stock_logs && data.bundle_stock_logs.length
                    "
                    class="font a4-bundle"
                  >
                    <div
                      v-for="(bundle, bIdx) in data.bundle_stock_logs"
                      :key="`b-${index}-${bIdx}`"
                      class="a4-bundle-row"
                    >
                      - {{ bundle.bundle_item_product.name }} x
                      {{ bundle.bundle_qty }}
                    </div>
                  </div>
                </td>
                <td class="font text-center">{{ data.qty | ToNumber }}</td>
                <td class="font text-center">
                  {{ getUnit(data) }}
                </td>
                <td class="font text-center">
                  <template v-if="isStampRewardLine(safeItem, data)">
                    {{ $t("free") }}
                  </template>
                  <template v-else>
                    {{ data.price_sale | NumberFormat }}
                  </template>
                </td>
                <td class="font text-right">
                  <template v-if="isStampRewardLine(safeItem, data)">
                    {{ $t("free") }}
                  </template>
                  <template v-else>
                    {{ data.total_price | NumberFormat }}
                  </template>
                </td>
              </tr>

              <template v-if="hasBuffetPlans">
                <tr>
                  <td colspan="6" class="font font-weight-bold">
                    {{ $t("buffet_plans") }} ({{ $t("buffet_persons") }}
                    {{ buffetTotalPersons }})
                  </td>
                </tr>
                <tr
                  v-for="(plan, pidx) in receiptBuffetPlans"
                  :key="`bp-${pidx}`"
                >
                  <td class="font text-center">
                    {{ (safeItem.history || []).length + pidx + 1 }}
                  </td>
                  <td class="font text-left">{{ plan.plan_name }}</td>
                  <td class="font text-center">{{ plan.persons | ToNumber }}</td>
                  <td class="font text-center">{{ $t("buffet_persons") }}</td>
                  <td class="font text-center">
                    {{ plan.unit_price | NumberFormat }}
                  </td>
                  <td class="font text-right">
                    {{ plan.subtotal | NumberFormat }}
                  </td>
                </tr>
              </template>

              <!-- Totals rows merged into the main table -->
              <tr v-if="subtotal">
                <td colspan="5" class="font text-right">
                  {{ $t("subtotal") }}
                </td>
                <td class="font text-right">{{ subtotal }}</td>
              </tr>
              <tr v-if="safeItem?.total_discount">
                <td colspan="5" class="font text-right">
                  {{ $t("discount") }}
                </td>
                <td class="font text-right">{{ total_discount }}</td>
              </tr>
              <tr v-if="tax_include">
                <td colspan="5" class="font text-right">VAT {{ tax }}</td>
                <td class="font text-right">
                  {{ safeItem?.tax_amount | NumberFormat }}
                </td>
              </tr>
              <tr v-if="grand_total">
                <td colspan="5" class="font text-right font-weight-bold">
                  <h3>
                    {{ $t("grand_total") }}
                  </h3>
                </td>
                <td class="font text-right font-weight-bold">
                  <h3>{{ grand_total }} {{ currency }}</h3>
                </td>
              </tr>
              <tr v-if="showPaymentCurrency">
                <td colspan="5" class="font text-right">
                  {{ $t("total_paid") }}
                </td>
                <td class="font text-right">
                  {{ total_paid_formatted }} {{ paymentCurrency }}
                </td>
              </tr>
              <tr v-if="cash_back">
                <td colspan="5" class="font text-right">
                  {{ $t("cash_back") }}
                </td>
                <td class="font text-right">{{ cash_back }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <v-row class="mt-3">
          <v-col cols="4">
            <div class="font default">{{ $t("receipt_by") }}</div>
          </v-col>
          <v-col cols="4">
            <div class="font default">{{ $t("product_received") }}</div>
          </v-col>
          <v-col cols="4">
            <div class="font default">{{ $t("buyer") }}</div>
          </v-col>
        </v-row>

        <v-row v-if="show_amount_letter" class="mt-12">
          <v-col cols="12">
            <div class="signature">
              <div class="font default">
                {{ $t("amount_letter") }}:
                <span class="font-weight-bold">
                  {{ grand_total_number | numberToLetter }}
                </span>
                {{ currency }}
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row v-if="show_bank_account && displayAccounts.length > 0">
          <v-col cols="6">
            <div class="font default">
              {{ $t("bank_account") }}
            </div>
            <p
              v-for="(account, index) in displayAccounts"
              :key="index"
              class="font default"
            >
              {{ account.bank_name }} : {{ account.account_no }}
              {{ account.account_name }} ({{ account.currency }})
            </p>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <div class="font default text-center">
              {{ footer_text }}
            </div>
          </v-col>
        </v-row>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import { myMixin } from "@/mixin/main";
import { formatBillQueue } from "@/utils/billQueue";

export default {
  name: "AppReceiptA4",
  mixins: [myMixin],
  props: {
    item: Object,
    show_logo: Boolean,
    branch: Boolean,
    customer_point: Boolean,
    customer_phone: Boolean,
    customer_address: Boolean,
    thb_currency: Boolean,
    usd_currency: Boolean,
    tax_include: Boolean,
    show_qr: Boolean,
    footer_text: String,
    customer_logistics: Boolean,
    outlined: {
      type: Boolean,
      default: false,
    },
    accounts: {
      type: Array,
      default: () => [],
    },
    show_email: {
      type: Boolean,
      default: true,
    },
    show_bank_account: {
      type: Boolean,
      default: true,
    },
    show_amount_letter: {
      type: Boolean,
      default: true,
    },
    show_phone_number: {
      type: Boolean,
      default: true,
    },
    show_mobile_number: {
      type: Boolean,
      default: true,
    },
    show_store_address: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      accountsData: [],
      head1: "ສາທາລະນະລັດ ປະຊາທິປະໄຕ ປະຊາຊົນລາວ",
      head2: "ສັນຕິພາບ ເອກະລາດ ປະຊາທິປະໄຕ ເອກະພາບ ວດັທະນາຖາວອນ",
    };
  },
  computed: {
    ...mapGetters({
      user: "auth/user",
    }),
    queueLabel() {
      return formatBillQueue(this.item?.queue_no);
    },
    safeItem() {
      if (!this.item) return null;
      if (!this.item.store_id) {
        this.item.store_id = { name: this.$t("store_name") };
      }
      if (!this.item.user_id) {
        this.item.user_id = { first_name: this.$t("unknown") };
      }
      if (this.item.history) {
        this.item.history = this.item.history.map((historyItem) => {
          if (!historyItem.product_id) {
            historyItem.product_id = { name: this.$t("product_name") };
          }
          return historyItem;
        });
      }
      return this.item;
    },
    receiptBuffetPlans() {
      const r = this.safeItem;
      if (!r) return [];
      if (Array.isArray(r.buffet_plans) && r.buffet_plans.length) {
        return r.buffet_plans;
      }
      if (Array.isArray(r.buffetPlans) && r.buffetPlans.length) {
        return r.buffetPlans;
      }
      return [];
    },
    hasBuffetPlans() {
      return this.receiptBuffetPlans.length > 0;
    },
    buffetTotalPersons() {
      return this.receiptBuffetPlans.reduce(
        (sum, p) => sum + (Number(p.persons) || 0),
        0
      );
    },
    getProductDisplayName() {
      return (data) => {
        if (!data || !data.product_id) {
          return this.$t("product_name");
        }
        let productName = data.product.name || this.$t("product_name");
        if (data.product_variant) {
          let options = [];
          if (
            data.product_variant.optionValues &&
            typeof data.product_variant.optionValues === "object"
          ) {
            options = Object.values(data.product_variant.optionValues);
          } else if (
            data.product_variant.options &&
            Array.isArray(data.product_variant.options)
          ) {
            options = data.product_variant.options.map(
              (option) => option.selected_value
            );
          }
          if (options.length > 0) {
            productName = `${productName} (${options.join(", ")})`;
          }
        }
        return productName;
      };
    },
    Img() {
      if (this.user && this.user.stores && this.user.stores.logo) {
        if (!this.user.stores.logo.includes("default")) {
          return Help.img(this.user.stores.logo);
        }
      }
    },
    branchMsg() {
      if (this.safeItem?.branch_id && this.safeItem.branch_id.name) {
        return `${this.safeItem.branch_id.name}`;
      }
      return "";
    },
    customer() {
      let text = "";
      if (this.safeItem?.customer_id && this.safeItem.customer_id.first_name) {
        text += `${this.safeItem.customer_id.first_name}`;
      }
      if (this.customerPhone) {
        text += ` | ${this.customerPhone}`;
      }
      if (this.customerPoint) {
        text += ` | ${this.customerPoint}`;
      }

      return text;
    },
    customerPoint() {
      if (this.safeItem?.customer_id && this.safeItem.customer_id.point) {
        return `${this.$options.filters.NumberFormat(
          this.safeItem.customer_id.point
        )}`;
      }
      return "";
    },
    customerPhone() {
      if (this.safeItem?.customer_id && this.safeItem.customer_id.phone_no) {
        return this.safeItem.customer_id.phone_no;
      }
      return "";
    },
    address() {
      if (
        this.safeItem?.customer_id?.village?.vill_name &&
        this.safeItem?.customer_id?.village?.district?.dr_name &&
        this.safeItem?.customer_id?.village?.district?.province?.pr_name
      ) {
        const village = this.safeItem.customer_id.village;
        const district = village.district;
        const province = district.province;
        return `${village.vill_name}, ${district.dr_name}, ${province.pr_name}`;
      }
      return "";
    },
    customerLogistics() {
      if (
        this.safeItem?.customer_id?.logistics?.name &&
        this.safeItem?.customer_id?.address
      ) {
        const logistics = this.safeItem.customer_id.logistics.name;
        const address = this.safeItem.customer_id.address;
        return `${logistics} - ${address}`;
      }
      return "";
    },
    storePhone() {
      if (this.safeItem?.store_id?.phone_no) {
        return this.safeItem.store_id.phone_no;
      }
      return "";
    },
    storeMobile() {
      if (this.safeItem?.store_id?.mobile_no) {
        return this.safeItem.store_id.mobile_no;
      }
      return "";
    },
    phoneMobileLine() {
      const parts = [];
      if (this.show_phone_number && this.storePhone) {
        parts.push(this.storePhone);
      }
      if (this.show_mobile_number && this.storeMobile) {
        parts.push(this.storeMobile);
      }
      return parts.join(" / ");
    },
    thRate() {
      if (
        this.safeItem &&
        this.safeItem.store_id &&
        this.safeItem.store_id.thb
      ) {
        const thb = this.safeItem.store_id.thb;
        const total = this.safeItem.total_price;
        return this.$options.filters.NumberFormat(total / thb);
      }
      return 0;
    },
    usRate() {
      if (
        this.safeItem &&
        this.safeItem.store_id &&
        this.safeItem.store_id.usd
      ) {
        const usd = this.safeItem.store_id.usd;
        const total = this.safeItem.total_price;
        return this.$options.filters.NumberFormat(total / usd);
      }
      return 0;
    },
    cash_back() {
      // total_price from backend already has discount subtracted
      if (
        this.safeItem &&
        this.safeItem.total_price &&
        this.safeItem.total_paid > 0
      ) {
        const actualAmount = this.safeItem.total_price; // Already discounted
        const tolerance = 0.01;
        const difference = Math.abs(this.safeItem.total_paid - actualAmount);
        if (difference <= tolerance) {
          return 0;
        } else {
          const cashBack = this.safeItem.total_paid - actualAmount;
          if (cashBack > 0) {
            return `- ${this.$options.filters.NumberFormat(cashBack)} ${
              this.currency
            }`;
          }
        }
        return 0;
      }
      return 0;
    },
    subtotal() {
      // Use total_price_sale if available (original price before discount)
      if (this.safeItem && this.safeItem.total_price_sale) {
        return this.$options.filters.NumberFormat(
          this.safeItem.total_price_sale
        );
      }
      // Fallback: calculate original price as total_price + total_discount
      if (
        this.safeItem &&
        this.safeItem.total_price &&
        this.safeItem.total_discount
      ) {
        return this.$options.filters.NumberFormat(
          this.safeItem.total_price + this.safeItem.total_discount
        );
      }
      return 0;
    },
    total_discount() {
      if (this.safeItem && this.safeItem.total_discount) {
        return `- ${this.$options.filters.NumberFormat(
          this.safeItem.total_discount
        )}`;
      }
      return 0;
    },
    grand_total() {
      // total_price from backend already has discount subtracted
      // Backend calculates: total_price = (total_price_sale - total_discount) + tax_amount
      if (this.safeItem && this.safeItem.total_price) {
        return this.$options.filters.NumberFormat(this.safeItem.total_price);
      }
      return 0;
    },
    grand_total_number() {
      // total_price from backend already has discount subtracted
      if (this.safeItem && this.safeItem.total_price) {
        return this.safeItem.total_price;
      }
      return 0;
    },
    currency() {
      if (
        this.safeItem &&
        this.safeItem.history &&
        this.safeItem.history.length > 0 &&
        this.safeItem.history[0].product &&
        this.safeItem.history[0].product.currency
      ) {
        return this.safeItem.history[0].product.currency.code;
      }
      if (
        this.safeItem &&
        this.safeItem.currency_id &&
        this.safeItem.currency_id.code
      ) {
        return this.safeItem.currency_id.code;
      }
      return "LAK";
    },
    paymentCurrency() {
      return this.currency;
    },
    showPaymentCurrency() {
      return false;
    },
    displayAccounts() {
      return this.accounts && this.accounts.length > 0
        ? this.accounts
        : this.accountsData;
    },
  },
  mounted() {
    if (
      this.show_bank_account &&
      (!this.accounts || this.accounts.length === 0)
    ) {
      this.fetchBankAccount();
    }
  },
  methods: {
    async fetchBankAccount() {
      try {
        const { data } = await this.$http.get("/bank_accounts");
        if (data && data.data) {
          this.accountsData = data.data;
        }
      } catch (error) {
        console.log(error);
      }
    },
    getUnit(data) {
      if (data.product?.size?.unit) {
        return this.$t(data.product.size.unit);
      }
      return this.$t("unit_pcs") || "pcs";
    },
  },
};
</script>

<style scoped>
.a4-container {
  padding: 24px;
}
.a4-header .logo {
  object-fit: contain;
}

.a4-bundle {
  color: #777;
  margin-top: 4px;
}
.a4-bundle-row {
  line-height: 1.3;
}
.stamp-reward-tag {
  font-weight: bold;
  margin-left: 4px;
}
.a4-totals {
  max-width: 420px;
  margin-left: auto;
}
.a4-total-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
}
.a4-grand {
  font-weight: 700;
  border-top: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
}
.a4-signatures {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
}
.a4-signatures .sig-line {
  display: flex;
  align-items: center;
  gap: 12px;
}
.a4-signatures .line {
  flex: 1;
  height: 1px;
  background: #333;
}
.a4-signatures .label {
  white-space: nowrap;
}
@media print {
  .a4-container {
    width: 210mm;
    min-height: 297mm;
    padding: 15mm;
  }
}
</style>
